import "@/App.css";
import React, { Suspense, lazy } from "react";
import axios from "axios";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { installCacheAutoBust } from "@/lib/api-cache";

// Phase 2.46.167 — install global cache auto-bust so any POST/PUT/DELETE
// invalidates related GET caches without every mutation site needing to
// call bustCache() explicitly.
installCacheAutoBust(axios);

// Phase 2.46.115 — Route-level code-splitting. Before this, every user
// (even on /login) downloaded a single bundle that included the 668 KB
// StudyDetailPage.jsx + 196 KB DataEntryPage.jsx + every other page —
// ~2 MB of JS just to render the login screen. By lazy-loading each
// route, the initial bundle drops to the Login/Layout shell and each
// page-specific chunk loads only when its route is visited.
//
// LoginPage stays eager because it's the most common cold entry point;
// pre-fetching kicks in for everything else once the user authenticates.
import LoginPage from "@/pages/LoginPage";

const ForgotPasswordPage   = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage    = lazy(() => import("@/pages/ResetPasswordPage"));
const DashboardPage        = lazy(() => import("@/pages/DashboardPage"));
const StudiesPage          = lazy(() => import("@/pages/StudiesPage"));
const StudyDetailPage      = lazy(() => import("@/pages/StudyDetailPage"));
const StudyBySlugPage      = lazy(() => import("@/pages/StudyBySlugPage"));
const SitesPage            = lazy(() => import("@/pages/SitesPage"));
const SubjectsPage         = lazy(() => import("@/pages/SubjectsPage"));
const ECRFBuilderPage      = lazy(() => import("@/pages/ECRFBuilderPage"));
const DataEntryPage        = lazy(() => import("@/pages/DataEntryPage"));
const VisitsPage           = lazy(() => import("@/pages/VisitsPage"));
const AESAEPage            = lazy(() => import("@/pages/AESAEPage"));
const QueryManagementPage  = lazy(() => import("@/pages/QueryManagementPage"));
const AuditTrailPage       = lazy(() => import("@/pages/AuditTrailPage"));
const UserManagementPage   = lazy(() => import("@/pages/UserManagementPage"));
const ReportsPage          = lazy(() => import("@/pages/ReportsPage"));
const ValidationDocsPage   = lazy(() => import("@/pages/ValidationDocsPage"));
const SystemHealthPage     = lazy(() => import("@/pages/SystemHealthPage"));
const ChangePasswordPage   = lazy(() => import("@/pages/ChangePasswordPage"));

function FullPageSpinner({ label = "Loading AMDiris..." }) {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0A1628 0%, #0D1F3C 100%)" }} data-testid="route-loading-spinner">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#00B4D8] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-slate-400 text-sm">{label}</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<FullPageSpinner />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
            <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              <Route path="studies" element={<StudiesPage />} />
              <Route path="studies/:studyId" element={<StudyDetailPage />} />
              <Route path="study/:slug" element={<StudyBySlugPage />} />
              <Route path="sites" element={<SitesPage />} />
              <Route path="subjects" element={<SubjectsPage />} />
              <Route path="ecrf-builder" element={<ECRFBuilderPage />} />
              <Route path="data-entry" element={<DataEntryPage />} />
              <Route path="visits" element={<VisitsPage />} />
              <Route path="ae-sae" element={<AESAEPage />} />
              <Route path="queries" element={<QueryManagementPage />} />
              <Route path="audit-trail" element={<AuditTrailPage />} />
              <Route path="users" element={<UserManagementPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="validation" element={<ValidationDocsPage />} />
              <Route path="system-health" element={<SystemHealthPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
